import { Autocomplete, MenuItem, TextField } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import React, { useEffect } from 'react';
import {
	Controller,
	FieldValues,
	UseControllerProps,
	useFormContext,
} from 'react-hook-form';
import { SelectFieldConfiguration } from '../../../api/ConfigApi';
import FieldLabel from '../../fields/FieldLabel';

type FieldProps = Omit<SelectFieldConfiguration, 'name'>;
interface Props<T> extends FieldProps, UseControllerProps<T> {
	save?: (currValue: string, fieldName: string) => void;
	fieldHasExternalDataCalls?: boolean;
}

const SelectField = <T extends FieldValues>(props: Props<T>) => {
	const {
		control,
		name,
		title,
		optional,
		values: options,
		disabled,
		save,
		fieldHasExternalDataCalls,
	} = props;

	const { setValue, getValues } = useFormContext();

	// if only one option is available, set it as the value (should only occur once)
	useEffect(() => {
		if (!optional && options?.length === 1 && getValues(name) === '') {
			setValue(name, options[0] as any, { shouldDirty: true });
			// trigger a save to fetch data from external data call, if applicable
			if (fieldHasExternalDataCalls) {
				save && save(options[0], name);
			}
		}
	});

	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState: { error } }) => (
				<FormControl error={!!error} sx={{ width: '100%' }}>
					<FieldLabel title={title} required={!optional} htmlFor={name} />
					<Autocomplete
						{...field}
						onChange={(_event, newValue) => {
							field.onChange(newValue);
						}}
						id={name}
						value={field.value || null}
						options={optional ? ['', ...options] : options}
						getOptionLabel={(option) => option ?? ''}
						disabled={disabled}
						disableClearable={true}
						openOnFocus={true}
						autoHighlight={true}
						isOptionEqualToValue={(option, value) => {
							return option === '' ? false : option === value;
						}}
						renderOption={(props, option) => {
							// for optional fields, render a blank option
							return (
								<MenuItem {...props} value={option}>
									{option === '' ? <em>None</em> : option}
								</MenuItem>
							);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								fullWidth
								required={!optional}
								error={!!error}
								helperText={error?.message}
								disabled={disabled}
								onBlur={(e) => {
									field.onBlur();
									save && save(e.target.value, name);
								}}
								sx={{
									'& .MuiInput-input:focus': {
										backgroundColor: 'transparent',
									},
								}}
							/>
						)}
					/>
				</FormControl>
			)}
		/>
	);
};

export default SelectField;
